import React, { useState, useEffect } from 'react';
import { api } from '../api';
import ExternalPaymentsTab from './ExternalPaymentsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PublicPageSettings = ({ user, hoaData }) => {
  const [activeTab, setActiveTab] = useState('settings'); // 'settings' or 'payments'
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [availableDocuments, setAvailableDocuments] = useState([]);
  const [availableNewsletters, setAvailableNewsletters] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(true);

  useEffect(() => {
    loadProfile();
    loadAvailableDocuments();
    loadAvailableNewsletters();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await api.get(`/api/public-profile/${user.hoa_id}`);
      
      // Ensure public_documents is initialized properly
      const profileData = response.data;
      if (!profileData.public_documents) {
        profileData.public_documents = {
          section_title: 'Community Documents',
          document_ids: []
        };
      }
      if (!profileData.public_documents.document_ids) {
        profileData.public_documents.document_ids = [];
      }
      
      setProfile(profileData);
      console.log('Profile loaded:', profileData);
    } catch (error) {
      console.error('Failed to load public profile:', error);
      alert('Failed to load public profile: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableDocuments = async () => {
    try {
      setLoadingDocs(true);
      // Use user.hoa_id instead of profile.hoa_id to avoid null reference error
      const hoaId = user?.hoa_id;
      if (!hoaId) {
        console.error('No HOA ID available');
        setAvailableDocuments([]);
        return;
      }
      
      // Get ALL documents from ALL sources (hoa_documents + documents collections)
      const { data } = await api.get(`/api/public-documents-sync/hoa/${hoaId}/all-documents`);
      
      if (data && data.documents && Array.isArray(data.documents)) {
        console.log('📄 Loaded documents from all sources:', {
          total: data.total,
          sources: data.sources
        });
        
        // Ensure documents have proper document_id field
        const normalizedDocs = data.documents.map(d => ({
          ...d,
          document_id: d.document_id || d.id
        }));
        
        setAvailableDocuments(normalizedDocs);
      } else {
        setAvailableDocuments([]);
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
      alert('Failed to load documents: ' + (error.response?.data?.detail || error.message));
      setAvailableDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const loadAvailableNewsletters = async () => {
    try {
      // Fetch published newsletters only for public page
      const response = await api.get(`/api/newsletter?hoa_id=${user.hoa_id}&status=published`);
      setAvailableNewsletters(response.data.newsletters || []);
    } catch (error) {
      console.error('Failed to load newsletters:', error);
      alert('Failed to load newsletters: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSave = async () => {
    console.log('🔵 handleSave called');
    console.log('📦 Profile data:', profile);
    setSaving(true);
    try {
      console.log('🔄 Sending POST to /api/public-profile/' + user.hoa_id);
      const response = await api.post(`/api/public-profile/${user.hoa_id}`, profile);
      console.log('✅ Save response:', response.data);
      
      // Update slug if it changed
      if (response.data.slug) {
        setProfile({ ...profile, slug: response.data.slug });
      }
      
      // ENHANCED: Automatically sync selected documents to public visibility
      const selectedDocIds = profile?.public_documents?.document_ids || [];
      if (selectedDocIds.length > 0) {
        console.log('📄 Syncing', selectedDocIds.length, 'documents to public visibility...');
        
        try {
          const syncResponse = await api.post('/api/public-profile/sync-selected-documents', {
            hoa_id: user.hoa_id,
            document_ids: selectedDocIds
          });
          
          console.log('✅ Document sync result:', syncResponse.data);
          
          const synced = syncResponse.data.documents_synced || 0;
          const failed = syncResponse.data.failed_documents?.length || 0;
          
          let message = 'Public Page Saved!';
          if (synced > 0) {
            message += `\n✅ ${synced} document(s) marked as publicly viewable`;
          }
          
          alert(message);
        } catch (syncError) {
          console.error('⚠️ Document sync warning:', syncError);
          alert('Public Page Saved!');
        }
      } else {
        alert('Public Page Saved!');
      }
    } catch (error) {
      console.error('❌ Failed to save:', error);
      console.error('❌ Error response:', error.response);
      alert('Failed to save public page: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await api.post(`/api/public-profile/${user.hoa_id}/header-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      await loadProfile();
      alert('Header image uploaded successfully!');
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingImage(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!confirm('Are you sure you want to remove the header image?')) return;

    try {
      await api.delete(`/api/public-profile/${user.hoa_id}/header-image`);

      await loadProfile();
      alert('Header image removed');
    } catch (error) {
      console.error('Failed to delete image:', error);
      alert('Failed to delete image: ' + (error.response?.data?.detail || error.message));
    }
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  if (!profile) {
    return <div className="p-6 text-red-600">Failed to load profile</div>;
  }

  const publicUrl = profile.slug ? `${window.location.origin}/p/${profile.slug}` : '';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-blue-900 mb-2">Public HOA Page</h3>
        <p className="text-sm text-blue-700 mb-2">
          Create a public-facing page for your HOA that anyone can view without logging in.
        </p>
        {publicUrl && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-blue-900">Public URL:</span>
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:underline"
            >
              {publicUrl}
            </a>
          </div>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-2 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'settings'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Public Page Settings
            </button>
            <button
              onClick={() => setActiveTab('payments')}
              className={`py-2 px-4 border-b-2 font-medium text-sm ${
                activeTab === 'payments'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              External Payments
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'payments' ? (
        <ExternalPaymentsTab user={user} hoaData={hoaData} />
      ) : (
        <div className="space-y-6">
          {/* Basic Information */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Basic Information</h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">HOA Name *</div>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={profile.name || ''}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
            />
          </label>

          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">Headline</div>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={profile.headline || ''}
              onChange={(e) => setProfile({ ...profile, headline: e.target.value })}
              placeholder="Welcome to [HOA Name]"
            />
          </label>
        </div>

        <label className="block">
          <div className="text-sm font-medium text-gray-700 mb-1">About</div>
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={5}
            value={profile.about || ''}
            onChange={(e) => setProfile({ ...profile, about: e.target.value })}
            placeholder="Describe your community..."
          />
        </label>
      </div>

      {/* Address */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Address</h3>
        
        <div className="grid md:grid-cols-2 gap-4">
          <label className="block md:col-span-2">
            <div className="text-sm font-medium text-gray-700 mb-1">Street Address</div>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={profile.address?.line1 || ''}
              onChange={(e) => setProfile({
                ...profile,
                address: { ...profile.address, line1: e.target.value }
              })}
            />
          </label>

          <label className="block md:col-span-2">
            <div className="text-sm font-medium text-gray-700 mb-1">Address Line 2</div>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={profile.address?.line2 || ''}
              onChange={(e) => setProfile({
                ...profile,
                address: { ...profile.address, line2: e.target.value }
              })}
            />
          </label>

          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">City</div>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={profile.address?.city || ''}
              onChange={(e) => setProfile({
                ...profile,
                address: { ...profile.address, city: e.target.value }
              })}
            />
          </label>

          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">State</div>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={profile.address?.state || ''}
              onChange={(e) => setProfile({
                ...profile,
                address: { ...profile.address, state: e.target.value }
              })}
              placeholder="FL"
            />
          </label>

          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">ZIP Code</div>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={profile.address?.zip || ''}
              onChange={(e) => setProfile({
                ...profile,
                address: { ...profile.address, zip: e.target.value }
              })}
            />
          </label>
        </div>
      </div>

      {/* Contact Information */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Contact Information</h3>
        
        <div className="grid md:grid-cols-3 gap-4">
          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">Public Email</div>
            <input
              type="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={profile.contact?.email || ''}
              onChange={(e) => setProfile({
                ...profile,
                contact: { ...profile.contact, email: e.target.value }
              })}
              placeholder="info@hoa.com"
            />
          </label>

          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">Phone Number</div>
            <input
              type="tel"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={profile.contact?.phone || ''}
              onChange={(e) => setProfile({
                ...profile,
                contact: { ...profile.contact, phone: e.target.value }
              })}
              placeholder="(555) 555-1234"
            />
          </label>

          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">Website (Optional)</div>
            <input
              type="url"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={profile.contact?.website || ''}
              onChange={(e) => setProfile({
                ...profile,
                contact: { ...profile.contact, website: e.target.value }
              })}
              placeholder="https://..."
            />
          </label>
        </div>
      </div>

      {/* Map Settings */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Map Settings</h3>
        
        <div className="grid md:grid-cols-[1fr_auto] gap-4">
          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">Map Location Query</div>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={profile.map?.place_query || ''}
              onChange={(e) => setProfile({
                ...profile,
                map: { ...profile.map, place_query: e.target.value }
              })}
              placeholder="Full address or place name"
            />
            <div className="text-xs text-gray-500 mt-1">
              e.g., "123 Main St, City, State ZIP" or "HOA Clubhouse, City"
            </div>
          </label>

          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">Map Provider</div>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={profile.map?.provider || 'google'}
              onChange={(e) => setProfile({
                ...profile,
                map: { ...profile.map, provider: e.target.value }
              })}
            >
              <option value="google">Google Maps</option>
              <option value="apple">Apple Maps</option>
            </select>
          </label>
        </div>

        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={profile.map?.show_map ?? false}
            onChange={(e) => setProfile({
              ...profile,
              map: { ...profile.map, show_map: e.target.checked }
            })}
          />
          <span className="text-sm font-medium text-gray-700">Show map on public page</span>
        </label>
      </div>

      {/* Header Image */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Header Image</h3>
        
        {profile.header_image_id && (
          <div className="mb-4">
            <img
              src={`${BACKEND_URL}/api/public-profile/asset/${profile.header_image_id}`}
              alt="Header"
              className="max-w-full h-48 object-cover rounded-lg border border-gray-200"
            />
            <button
              onClick={handleDeleteImage}
              className="mt-2 px-3 py-1 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
            >
              Remove Image
            </button>
          </div>
        )}

        <div>
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            disabled={uploadingImage}
            className="text-sm text-gray-600"
          />
          {uploadingImage && <span className="ml-2 text-sm text-gray-500">Uploading...</span>}
        </div>
      </div>

      {/* Public Documents Section - RECREATED FROM SCRATCH */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="border-b pb-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-900">📄 Public Documents</h3>
          <p className="text-sm text-gray-600 mt-1">Select documents to display on your public page for easy community access</p>
        </div>

        {/* Section Title Editor */}
        <div className="mb-4">
          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">Section Title (Editable)</div>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={profile.public_documents?.section_title || 'Community Documents'}
              onChange={(e) => {
                const newTitle = e.target.value;
                setProfile(prev => ({
                  ...prev,
                  public_documents: {
                    document_ids: prev.public_documents?.document_ids || [],
                    section_title: newTitle
                  }
                }));
              }}
              placeholder="e.g., Community Documents, Governing Documents, HOA Resources"
            />
          </label>
        </div>

        {loadingDocs ? (
          <div className="text-center py-4 text-gray-500">Loading documents...</div>
        ) : availableDocuments.length === 0 ? (
          <div className="text-center py-4 text-gray-500">No documents available. Upload documents in Documents tab and set visibility to "All Homeowners".</div>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {availableDocuments.map((doc) => {
              if (!doc.document_id) return null;
              
              const currentlySelected = (profile.public_documents?.document_ids || []).indexOf(doc.document_id) !== -1;
              
              return (
                <div key={`doc-item-${doc.document_id}`} className="flex items-center gap-3 p-3 border rounded-lg hover:bg-gray-50">
                  <input
                    type="checkbox"
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                    checked={currentlySelected}
                    onChange={(e) => {
                      e.stopPropagation();
                      const shouldAdd = e.target.checked;
                      const targetDocId = doc.document_id;
                      
                      setProfile(previousProfile => {
                        const existingIds = previousProfile.public_documents?.document_ids || [];
                        const existingTitle = previousProfile.public_documents?.section_title || 'Community Documents';
                        
                        let newIds;
                        if (shouldAdd) {
                          // Add if not present
                          if (existingIds.indexOf(targetDocId) === -1) {
                            newIds = [...existingIds, targetDocId];
                          } else {
                            newIds = existingIds;
                          }
                        } else {
                          // Remove
                          newIds = existingIds.filter(id => id !== targetDocId);
                        }
                        
                        console.log('Toggle doc:', targetDocId, 'shouldAdd:', shouldAdd, 'newIds:', newIds);
                        
                        return {
                          ...previousProfile,
                          public_documents: {
                            section_title: existingTitle,
                            document_ids: newIds
                          }
                        };
                      });
                    }}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-2xl">📄</span>
                      <div>
                        <div className="font-medium text-gray-900">{doc.title || 'Untitled'}</div>
                        <div className="text-xs text-gray-500">{doc.category || 'Uncategorized'}</div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Latest Newsletter Section */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="border-b pb-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-900">📰 Latest Newsletter</h3>
          <p className="text-sm text-gray-600 mt-1">Show the most recent newsletter on your public page</p>
        </div>

        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={profile.show_latest_newsletter || false}
            onChange={(e) => setProfile({
              ...profile,
              show_latest_newsletter: e.target.checked
            })}
          />
          <span className="text-sm font-medium text-gray-700">
            Display latest newsletter on public page
          </span>
        </label>

        {availableNewsletters.length > 0 && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="text-sm text-blue-900">
              <span className="font-medium">Latest Newsletter:</span> {availableNewsletters[0]?.title || 'Untitled'}
            </div>
            <div className="text-xs text-blue-700 mt-1">
              {availableNewsletters[0]?.created_at ? new Date(availableNewsletters[0].created_at).toLocaleDateString() : ''}
            </div>
          </div>
        )}

        {availableNewsletters.length === 0 && (
          <div className="text-sm text-gray-500 mt-2">
            No newsletters available yet. Create one in the Newsletter section.
          </div>
        )}
      </div>

      {/* Upcoming Public Meeting Section */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="border-b pb-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-900">📅 Upcoming Public Meeting</h3>
          <p className="text-sm text-gray-600 mt-1">Display information about the next public meeting</p>
        </div>

        <label className="inline-flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={profile.show_upcoming_meeting || false}
            onChange={(e) => setProfile({
              ...profile,
              show_upcoming_meeting: e.target.checked
            })}
          />
          <span className="text-sm font-medium text-gray-700">
            Display upcoming meeting on public page
          </span>
        </label>

        {profile.show_upcoming_meeting && (
          <div className="space-y-4 pl-6 border-l-4 border-blue-300">
            <div className="grid md:grid-cols-2 gap-4">
              <label className="block">
                <div className="text-sm font-medium text-gray-700 mb-1">Meeting Title</div>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  value={profile.upcoming_meeting?.title || ''}
                  onChange={(e) => setProfile({
                    ...profile,
                    upcoming_meeting: { ...profile.upcoming_meeting, title: e.target.value }
                  })}
                  placeholder="e.g., Monthly Board Meeting"
                />
              </label>

              <label className="block">
                <div className="text-sm font-medium text-gray-700 mb-1">Meeting Date & Time</div>
                <input
                  type="datetime-local"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                  value={profile.upcoming_meeting?.datetime || ''}
                  onChange={(e) => setProfile({
                    ...profile,
                    upcoming_meeting: { ...profile.upcoming_meeting, datetime: e.target.value }
                  })}
                />
              </label>
            </div>

            <label className="block">
              <div className="text-sm font-medium text-gray-700 mb-1">Location</div>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                value={profile.upcoming_meeting?.location || ''}
                onChange={(e) => setProfile({
                  ...profile,
                  upcoming_meeting: { ...profile.upcoming_meeting, location: e.target.value }
                })}
                placeholder="e.g., Community Clubhouse"
              />
            </label>

            <label className="block">
              <div className="text-sm font-medium text-gray-700 mb-1">Additional Details</div>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                rows={3}
                value={profile.upcoming_meeting?.details || ''}
                onChange={(e) => setProfile({
                  ...profile,
                  upcoming_meeting: { ...profile.upcoming_meeting, details: e.target.value }
                })}
                placeholder="Any additional information residents should know..."
              />
            </label>
          </div>
        )}
      </div>

      {/* Meeting Agenda Section */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="border-b pb-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-900">📋 Meeting Agenda</h3>
          <p className="text-sm text-gray-600 mt-1">Upload or link to the meeting agenda document</p>
        </div>

        <label className="inline-flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={profile.show_meeting_agenda || false}
            onChange={(e) => setProfile({
              ...profile,
              show_meeting_agenda: e.target.checked
            })}
          />
          <span className="text-sm font-medium text-gray-700">
            Display meeting agenda on public page
          </span>
        </label>

        {profile.show_meeting_agenda && (
          <div className="space-y-4 pl-6 border-l-4 border-green-300">
            <label className="block">
              <div className="text-sm font-medium text-gray-700 mb-1">Agenda Title</div>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                value={profile.meeting_agenda?.title || ''}
                onChange={(e) => setProfile({
                  ...profile,
                  meeting_agenda: { ...profile.meeting_agenda, title: e.target.value }
                })}
                placeholder="e.g., January 2025 Board Meeting Agenda"
              />
            </label>

            <label className="block">
              <div className="text-sm font-medium text-gray-700 mb-1">Link to Agenda Document (URL)</div>
              <input
                type="url"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                value={profile.meeting_agenda?.document_url || ''}
                onChange={(e) => setProfile({
                  ...profile,
                  meeting_agenda: { ...profile.meeting_agenda, document_url: e.target.value }
                })}
                placeholder="https://... or select from HOA documents below"
              />
            </label>

            <div className="text-sm text-gray-600 mb-2">Or select from HOA Documents:</div>
            {loadingDocs ? (
              <div className="text-sm text-gray-500">Loading documents...</div>
            ) : (
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                value={profile.meeting_agenda?.document_id || ''}
                onChange={(e) => {
                  const selectedDoc = availableDocuments.find(d => d.document_id === e.target.value);
                  setProfile({
                    ...profile,
                    meeting_agenda: { 
                      ...profile.meeting_agenda, 
                      document_id: e.target.value,
                      document_url: e.target.value ? `${BACKEND_URL}/api/hoa-files-pro/documents/${e.target.value}/view` : ''
                    }
                  });
                }}
              >
                <option value="">-- Select a document --</option>
                {availableDocuments.map((doc) => (
                  <option key={doc.document_id} value={doc.document_id}>
                    {doc.title} ({doc.category || 'Uncategorized'})
                  </option>
                ))}
              </select>
            )}

            {profile.meeting_agenda?.document_url && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-sm text-green-900 flex items-center gap-2">
                  <span className="text-xl">📋</span>
                  <span className="font-medium">Agenda document linked successfully</span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Voting Profiles / Nominees Section */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <div className="border-b pb-2 mb-4">
          <h3 className="text-lg font-semibold text-gray-900">🗳️ Election Voting Profiles</h3>
          <p className="text-sm text-gray-600 mt-1">Display candidate profiles for current elections on your public page</p>
        </div>

        <label className="inline-flex items-center gap-2 mb-4">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={profile.show_voting_profiles || false}
            onChange={(e) => setProfile({
              ...profile,
              show_voting_profiles: e.target.checked
            })}
          />
          <span className="text-sm font-medium text-gray-700">
            Show current election nominee profiles on public page
          </span>
        </label>

        {profile.show_voting_profiles && (
          <div className="space-y-3 pl-4 border-l-4 border-purple-300">
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
              <h4 className="font-medium text-purple-900 mb-2">📋 What will be displayed:</h4>
              <ul className="text-sm text-purple-800 space-y-1 list-disc list-inside">
                <li>Candidate names and positions they're running for</li>
                <li>Candidate bio/profile information (if provided)</li>
                <li>Only shows nominees from the <strong>current active election</strong></li>
                <li>Automatically hides when election ends or is archived</li>
              </ul>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-800">
                <span className="text-amber-600 font-medium">⚠️ Note:</span> Profiles will only appear after the 
                <strong> nomination period has closed</strong>. This ensures all candidates have a chance to 
                complete their profiles before public display.
              </p>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <h4 className="font-medium text-blue-900 mb-2">📄 Additional Voter Information</h4>
              <p className="text-sm text-blue-800">
                Extra voter information such as <strong>bylaw changes</strong> can be posted as documents 
                and selected in the "Visible Documents" section above for non-HOA Manager Pro users to access.
              </p>
              <p className="text-xs text-blue-700 mt-2 italic">
                For members inside the platform, they can also see postings in the Announcements section. 
                Note: Announcements are NOT posted to this external public page.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Visibility */}
      <div className="bg-white rounded-lg shadow p-6 space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">Visibility</h3>
        
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={profile.visibility === 'public'}
            onChange={(e) => setProfile({
              ...profile,
              visibility: e.target.checked ? 'public' : 'hidden'
            })}
          />
          <span className="text-sm font-medium text-gray-700">
            Make this page publicly visible
          </span>
        </label>
        <p className="text-xs text-gray-500">
          When unchecked, the page will be hidden from public view
        </p>
      </div>

      {/* External Payments Toggle */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg shadow p-6 space-y-3 border-2 border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-lg font-semibold text-gray-900 mb-1">💳 Enable External Payments</h4>
            <p className="text-sm text-gray-600">
              Allow visitors to make payments on your public page without logging in
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={profile.external_payments_enabled || false}
              onChange={(e) => setProfile({
                ...profile,
                external_payments_enabled: e.target.checked
              })}
            />
            <div className="w-14 h-8 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[4px] after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-green-600"></div>
          </label>
        </div>
        
        {profile.external_payments_enabled && (
          <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              ✅ <strong>Enabled:</strong> A "Make a Payment" button will appear on your public HOA page. Visitors can pay dues, fees, and other charges securely.
            </p>
          </div>
        )}
        
        {!profile.external_payments_enabled && (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">
              ℹ️ <strong>Disabled:</strong> External payments are hidden. Enable this after setting up Stripe Express in Finance Settings.
            </p>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>

        {profile.slug && profile.visibility === 'public' && (
          <a
            href={`/p/${profile.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="px-6 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
          >
            Preview Public Page
          </a>
        )}
      </div>
        </div>
      )}
    </div>
  );
};

export default PublicPageSettings;
